import { TShirt } from "@phosphor-icons/react"
import { useParams } from "@tanstack/react-router"
import { useState } from "react"

import { Button } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { useRemoveVariantsFromPurchasePlan } from "~/hooks/usePurchasePlan"
import { trackEvent } from "~/hooks/useTrackingService"

type Props = {
  variantIds: string[]
}

export const RemoveVariantsFromPurchasePlanDialog = ({ variantIds }: Props) => {
  const params = useParams({ strict: false })
  const [open, setOpen] = useState(false)

  const purchasePlanId = params.purchasePlanId ? Number(params.purchasePlanId) : null

  const { mutate: removeVariants, isPending: isRemovingVariants } =
    useRemoveVariantsFromPurchasePlan()

  if (!purchasePlanId) return null

  const handleOpenChange = (open: boolean) => {
    setOpen(open)
  }

  const onSubmit = () => {
    removeVariants(
      {
        purchasePlanId,
        variantIds,
      },
      {
        onSuccess: () => {
          trackEvent("Supply Plan Variants Removed", {
            "Number Of Variants": variantIds.length,
          })
          setOpen(false)
        },
      },
    )
  }

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger requires="edit" asChild>
        <Button size="sm" variant="neutral">
          <TShirt />
          Remove from supply plan
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove variants from supply plan</DialogTitle>
        </DialogHeader>
        <p className="text-sm">
          You are about to remove the selected variants from the supply plan. Are you sure you want
          to proceed?
        </p>
        <DialogFooter className="mt-4 justify-between">
          <DialogClose>
            <Button asChild variant="transparent">
              Cancel
            </Button>
          </DialogClose>
          <Button variant="danger" requires="edit" onClick={onSubmit} loading={isRemovingVariants}>
            {isRemovingVariants ? "Removing variants..." : "Remove variants"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
